import { hhmmFromTimeString } from "@/lib/datetime";
import {
  slotsIntervalsOverlap,
  type SlotInterval,
} from "@/lib/scheduleSlotOverlap";
import type { TimeSlot } from "@/types";

/** Slots of the day other than the one being edited (`null` when adding). */
function otherSlots(
  daySlots: TimeSlot[],
  editingIndex: number | null,
): TimeSlot[] {
  return daySlots.filter((_, index) => index !== editingIndex);
}

/** Another slot of the day already starts at the same HH:MM (blocks saving). */
export function hasDuplicateStart(
  daySlots: TimeSlot[],
  editingIndex: number | null,
  startTime: string,
): boolean {
  const start = hhmmFromTimeString(startTime);
  return otherSlots(daySlots, editingIndex).some(
    (slot) => hhmmFromTimeString(slot.start_time) === start,
  );
}

/** The candidate overlaps another slot of the day (soft warning only). */
export function overlapsOtherSlots(
  daySlots: TimeSlot[],
  editingIndex: number | null,
  candidate: SlotInterval,
): boolean {
  return otherSlots(daySlots, editingIndex).some((slot) =>
    slotsIntervalsOverlap(candidate, slot),
  );
}

/** Any two slots of the day overlap each other. */
export function daySlotsOverlap(slots: TimeSlot[]): boolean {
  return slots.some((slot, i) =>
    slots.slice(i + 1).some((other) => slotsIntervalsOverlap(slot, other)),
  );
}

/**
 * The day's slots after saving `slot` (replacing `editingIndex`, or appended
 * when adding), sorted by start time — the full list PUT for that day only.
 */
export function upsertDaySlot(
  daySlots: TimeSlot[],
  editingIndex: number | null,
  slot: TimeSlot,
): TimeSlot[] {
  const next = [...daySlots];
  if (editingIndex !== null) {
    next[editingIndex] = slot;
  } else {
    next.push(slot);
  }
  return next.sort((a, b) => a.start_time.localeCompare(b.start_time));
}
